import {FC} from 'react';

import {Chip, Skeleton, Box, Stack} from '@mui/material';
import {t} from 'i18next';
import {Else, If, Then, When} from 'react-if';

import Close from 'assets/icons/close';
import {TagSmIcon} from 'assets/icons/tagSmIcon';
import BasketDeliveryFee from 'components/basket-delivery-fee/BasketDeliveryFee';
import DeliveryFeeIcon from 'components/delivery-fee-icon/DeliveryFeeIcon';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import {BasketSummaryItemProps, PricesModel} from 'types';
import {formatPriceWithCurrency} from 'utils/currency';
import {StyledPointsChip} from 'views/basket-body/styles';
import ActivateEcode from 'views/basket-promotions/ActivateEcode';
import ProPointSuggestions from 'views/basket-promotions/ProPointSuggestions';
import {DiscountPopupIcon} from 'views/discount-popup/DiscountPopupDialog';
import {EstimatePointsIcon} from 'views/estimate-points-popup/EstimatePointsDialog';

export type BasketSummaryV4Props = {
  loading?: boolean;
};
const BasketSummaryV4: FC<BasketSummaryV4Props> = ({loading}) => {
  const basket = useAppSelector((state) => state?.basket?.basket);
  const promotions = useAppSelector((state) => state?.basket?.promotions);
  const session = useAppSelector((state) => state?.auth?.idToken);
  const voucherData = useAppSelector((state) => state?.app?.voucherData);
  const defaultMembershipCard = useAppSelector((state) => state?.app?.defaultMembershipCard);

  const items: BasketSummaryItemProps[] = [];
  const prices = basket?.prices as PricesModel;
  const loyaltyDetails = basket?.loyaltyResponseDetails;
  const discount = prices?.roundedDiscountAmount;
  const isAuthenticated = !!session;
  const voucherAmount = voucherData?.amount || 0;

  const showLoyaltyOptions = isAuthenticated && loyaltyDetails && defaultMembershipCard;
  const showPointsEarned = isAuthenticated && defaultMembershipCard;
  const hasProductsInBasket = basket?.basketItems.length;

  items.push({
    hasExtraOption: false,
    labelType: 'subtotal',
    label: t('label.priceBeforeDiscount'),
    price: formatPriceWithCurrency(prices?.totalOriginalPrice, basket?.priceUnit)
  });

  if (discount) {
    items.push({
      hasExtraOption: !!basket?.advancedPromotions && basket?.advancedPromotions?.length > 0,
      label: t('label.discount'),
      labelType: 'discount',
      priceHighlighted: true,
      price: formatPriceWithCurrency(-discount, basket?.priceUnit),
      options: {
        label: t('label.discount'),
        Icon: <DiscountPopupIcon advancedPromotions={basket?.advancedPromotions} />
      }
    });
  }

  items.push({
    hasExtraOption: true,
    label: t('label.delivery'),
    labelType: 'delivery',
    options: {
      label: t('label.fixedFee'),
      Icon: <DeliveryFeeIcon />
    },
    price: <BasketDeliveryFee />
  });

  if (basket?.voucherId && voucherAmount && !loyaltyDetails?.code) {
    items.push({
      hasExtraOption: !!voucherAmount,
      label: t('label.voucher'),
      labelType: 'voucher',
      priceHighlighted: true,
      price: formatPriceWithCurrency(-voucherAmount, basket.priceUnit),
      children: (
        <Chip
          label={basket.voucherId?.toUpperCase()}
          color="secondary"
          variant="outlined"
          // onDelete={() => removeVoucherCode(basket.voucherId || '', true)}
        />
      )
    });
  }

  if (showLoyaltyOptions && loyaltyDetails?.value > 0) {
    items.push({
      hasExtraOption: false,
      label: t('label.discount'),
      labelType: 'discount',
      priceHighlighted: true,
      price: formatPriceWithCurrency(loyaltyDetails?.value, basket.priceUnit),
      children: (
        <StyledPointsChip
          icon={<TagSmIcon />}
          label={t('label.makroProPoint')}
          color="secondary"
          variant="outlined"
          deleteIcon={<Close />}
        />
      )
    });
  }

  items.push({
    hasExtraOption: false,
    label: t('label.total'),
    labelType: 'total',
    price: formatPriceWithCurrency(prices?.totalFinalPriceToPay, basket?.priceUnit)
  });

  if (showPointsEarned && (promotions?.estimatedPointsReceive ?? 0) > 0) {
    items.push({
      hasExtraOption: true,
      label: t('label.estimatedPointsToEarn'),
      labelType: 'estimatedPointsToEarn',
      options: {
        label: t('label.fixedFee'),
        Icon: <EstimatePointsIcon estimatedPointsDetails={promotions?.estimatedPointsDetails ?? []} />
      },
      price: promotions.isLoading ? (
        <Skeleton animation="wave" variant="rounded" width={32} height={24} />
      ) : (
        (promotions?.estimatedPointsReceive ?? 0)
      )
    });
  }

  return (
    <If condition={loading}>
      <Then>
        <Box>
          <Stack spacing={1} height={400} padding="0px 24px 0px 24px">
            <Skeleton variant="rectangular" height="100%" sx={{borderRadius: 2}} />
          </Stack>
        </Box>
      </Then>
      <Else>
        <When
          condition={hasProductsInBasket && defaultMembershipCard?.cardNumber && ReactNative.isReactNativeWebView()}
        >
          <ProPointSuggestions />
        </When>
        <>
          <ActivateEcode />
        </>
      </Else>
    </If>
  );
};

export default BasketSummaryV4;
